/**
 * WorkflowsPage — Administration › Workflows (Story UI-U6 AC7).
 *
 * Three sections over EXISTING endpoints, each gated separately and hidden without
 * its read key:
 *   (a) Kanäle       — card list + Konfigurieren dialog (active / priority / fallback)
 *                      over GET /admin/channel-config + PUT …/channels/:channelKey.
 *   (b) Kanal-Matrix — table CRUD (docType × product × channel) over the matrix
 *                      endpoints.
 *   (c) Routing      — per-process auto/freigabe thresholds (string inputs) +
 *                      Funktionsgruppen-target select over confidence-policy +
 *                      group-routing.
 * The prototype's execution / success-rate stats have NO endpoint — honest omission.
 * status = dot + colored text (design.md §2); thresholds stay numeric strings.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  CHANNEL_CATALOG,
  DELIVERY_DOC_TYPES,
  confidencePolicyBandLabel,
  type ChannelConfigDto,
  type ChannelKey,
  type DeliveryDocType,
} from '@brokly/shared';
import { StatusDot } from '../../../components/ui/StatusDot';
import { Badge } from '../../../components/ui/Badge';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import {
  useChannelConfig,
  useChannelMatrix,
  useConfidencePolicy,
  useGroupRouting,
  useFunktionsgruppen,
} from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import {
  Button,
  Dialog,
  LoadingState,
  ErrorState,
  EmptyState,
  SettingRow,
  Toggle,
  TextInput,
} from '../components';
import type { ConfidencePolicyEntryDto } from '../types';

const meta = ADMIN_PAGE_META.workflows;

const ZUSTELL_CHANNELS = CHANNEL_CATALOG.filter((c) => c.zustellung);
const CHANNEL_LABEL: Record<string, string> = Object.fromEntries(
  CHANNEL_CATALOG.map((c) => [c.key, c.label]),
);
const DOC_TYPE_LABEL: Record<DeliveryDocType, string> = {
  mandat: 'Mandat',
  vertrag: 'Vertrag',
  rechnung: 'Rechnung',
  mahnung: 'Mahnung',
  korrespondenz: 'Korrespondenz',
};
const PROCESS_LABEL: Record<string, string> = {
  email_intake: 'E-Mail-Eingang',
  invoice_check: 'Rechnungskontrolle',
  commission_check: 'Courtage-Prüfung',
  zefix_enrich: 'Firmendaten (Zefix)',
  entity_resolution: 'Entitäts-Auflösung',
  signature_check: 'Unterschriftenprüfung',
  mandate_registration: 'Eintragungsbestätigung (Versicherer)',
  mandate_ruecklauf: 'Mandatsrücklauf',
  mandate_termination: 'Kündigung (Kunde)',
};
const processLabel = (p: string) => PROCESS_LABEL[p] ?? p;

// ── (a) Kanäle ──────────────────────────────────────────────────────────────
function ChannelDialog({
  channel,
  canManage,
  onClose,
}: {
  channel: ChannelConfigDto;
  canManage: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [active, setActive] = useState(channel.active);
  const [priority, setPriority] = useState(channel.priority == null ? '' : String(channel.priority));
  const [fallback, setFallback] = useState<string>(channel.fallback_channel ?? '');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiClient.admin.setChannelConfig(channel.channel_key, {
        active,
        priority: priority.trim() === '' ? null : Number(priority),
        fallbackChannel: fallback === '' ? null : (fallback as ChannelKey),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminChannelConfig() });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={channel.label}
      description="Kanal-Konfiguration für die Zustellung."
      footer={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          {canManage && (
            <Button disabled={save.isPending} onClick={() => save.mutate()}>
              Speichern
            </Button>
          )}
        </div>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="divide-y divide-border">
        <SettingRow label="Aktiv" hint="Kanal für die Zustellung verwenden.">
          <Toggle checked={active} onChange={setActive} disabled={!canManage} label="Aktiv" />
        </SettingRow>
        <SettingRow label="Priorität" hint="Reihenfolge in der Zustellkette (kleiner = früher).">
          <TextInput
            inputMode="numeric"
            value={priority}
            disabled={!canManage}
            onChange={(e) => setPriority(e.target.value)}
            className="w-24 tabular-nums"
          />
        </SettingRow>
        <SettingRow label="Fallback-Kanal" hint="Bei Nichterreichbarkeit.">
          <select
            aria-label="Fallback-Kanal"
            value={fallback}
            disabled={!canManage}
            onChange={(e) => setFallback(e.target.value)}
            className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="">(kein)</option>
            {ZUSTELL_CHANNELS.filter((c) => c.key !== channel.channel_key).map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
        </SettingRow>
      </div>
    </Dialog>
  );
}

function KanaeleSection({ canManage }: { canManage: boolean }) {
  const { data, isLoading, isError } = useChannelConfig();
  const [editing, setEditing] = useState<ChannelConfigDto | null>(null);
  const channels = data ?? [];

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;
  if (channels.length === 0) return <EmptyState label="Keine Kanäle konfiguriert." />;

  return (
    <>
      <div className="space-y-2">
        {channels.map((c) => (
          <div
            key={c.channel_key}
            className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{c.label}</span>
                {c.is_default && <Badge variant="neutral">Standard</Badge>}
              </div>
              <div className="mt-0.5">
                <StatusDot
                  tone={c.active ? 'success' : 'neutral'}
                  label={c.active ? 'Aktiv' : 'Inaktiv'}
                />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-4">
              {c.priority != null && (
                <span className="text-xs tabular-nums text-muted-foreground">
                  Prio {c.priority}
                </span>
              )}
              <Button variant="outline" size="sm" onClick={() => setEditing(c)}>
                {canManage ? 'Konfigurieren' : 'Ansehen'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {editing != null && (
        <ChannelDialog channel={editing} canManage={canManage} onClose={() => setEditing(null)} />
      )}
    </>
  );
}

// ── (b) Kanal-Matrix ────────────────────────────────────────────────────────
function MatrixSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useChannelMatrix();
  const [adding, setAdding] = useState(false);
  const [docType, setDocType] = useState<DeliveryDocType>(DELIVERY_DOC_TYPES[0]);
  const [product, setProduct] = useState('');
  const [channelKey, setChannelKey] = useState<ChannelKey>(ZUSTELL_CHANNELS[0]!.key);
  const rows = data ?? [];

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.adminChannelMatrix() });
  const create = useMutation({
    mutationFn: () =>
      apiClient.admin.createChannelMatrix({
        docType,
        product: product.trim() === '' ? null : product.trim(),
        channelKey,
      }),
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setProduct('');
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => apiClient.admin.deleteChannelMatrix(id),
    onSuccess: invalidate,
  });

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState />;

  return (
    <div className="space-y-3">
      {rows.length === 0 ? (
        <EmptyState label="Keine Zuordnungsregeln, es gilt die Standard-Zustellreihenfolge." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs font-medium text-muted-foreground">
                <th className="px-3 py-2">Dokumenttyp</th>
                <th className="px-3 py-2">Produkt/Sparte</th>
                <th className="px-3 py-2">Kanal</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="px-3 py-2">{DOC_TYPE_LABEL[r.doc_type] ?? r.doc_type}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.product ?? 'Alle'}</td>
                  <td className="px-3 py-2">{CHANNEL_LABEL[r.channel_key] ?? r.channel_key}</td>
                  <td className="px-3 py-2 text-right">
                    {canManage && (
                      <button
                        aria-label="Regel löschen"
                        disabled={del.isPending}
                        onClick={() => del.mutate(r.id)}
                        className="text-muted-foreground hover:text-status-danger disabled:opacity-50"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canManage &&
        (adding ? (
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Dokumenttyp</label>
              <select
                aria-label="Dokumenttyp"
                value={docType}
                onChange={(e) => setDocType(e.target.value as DeliveryDocType)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {DELIVERY_DOC_TYPES.map((d) => (
                  <option key={d} value={d}>
                    {DOC_TYPE_LABEL[d]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Produkt (optional)</label>
              <TextInput
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                placeholder="Alle"
                className="w-40"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">Kanal</label>
              <select
                aria-label="Kanal"
                value={channelKey}
                onChange={(e) => setChannelKey(e.target.value as ChannelKey)}
                className="h-9 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {ZUSTELL_CHANNELS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
              Hinzufügen
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Abbrechen
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" /> Regel hinzufügen
          </Button>
        ))}
    </div>
  );
}

// ── (c) Routing ─────────────────────────────────────────────────────────────
function RoutingRow({
  entry,
  groupId,
  groups,
  canManage,
}: {
  entry: ConfidencePolicyEntryDto;
  groupId: string | null;
  groups: { id: string; name: string }[];
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [auto, setAuto] = useState(entry.auto_threshold);
  const [freigabe, setFreigabe] = useState(entry.freigabe_threshold);
  const [group, setGroup] = useState<string>(groupId ?? '');
  const [error, setError] = useState<string | null>(null);
  // A process with a concept band (mandate_signing, Kap. 7.10) shows it; the api enforces it
  // and its German 400 message lands in `error` below.
  const band = confidencePolicyBandLabel(entry.process);

  const saveThresholds = useMutation({
    mutationFn: () =>
      apiClient.admin.setConfidencePolicy(entry.process, {
        auto_threshold: Number(auto),
        freigabe_threshold: Number(freigabe),
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminConfidencePolicy() });
    },
    onError: (e: Error) => setError(e.message),
  });
  const saveGroup = useMutation({
    mutationFn: (value: string) =>
      apiClient.admin.setGroupRouting(entry.process, value === '' ? null : value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminGroupRouting() }),
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-sm font-semibold">{processLabel(entry.process)}</span>
        {entry.is_default && <Badge variant="neutral">Standard</Badge>}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Auto-Schwelle ({band?.auto ?? '0 bis 1'})
          </label>
          <TextInput
            inputMode="decimal"
            value={auto}
            disabled={!canManage}
            onChange={(e) => setAuto(e.target.value)}
            className="tabular-nums"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">
            Freigabe-Schwelle ({band?.freigabe ?? '0 bis 1'})
          </label>
          <TextInput
            inputMode="decimal"
            value={freigabe}
            disabled={!canManage}
            onChange={(e) => setFreigabe(e.target.value)}
            className="tabular-nums"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-foreground">Funktionsgruppe</label>
          <select
            aria-label={`Funktionsgruppe für ${processLabel(entry.process)}`}
            value={group}
            disabled={!canManage}
            onChange={(e) => {
              setGroup(e.target.value);
              saveGroup.mutate(e.target.value);
            }}
            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="">(keine)</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}
      {canManage && (
        <div className="mt-3 flex justify-end">
          <Button size="sm" disabled={saveThresholds.isPending} onClick={() => saveThresholds.mutate()}>
            Schwellen speichern
          </Button>
        </div>
      )}
    </div>
  );
}

function RoutingSection({ canManage }: { canManage: boolean }) {
  const policyQuery = useConfidencePolicy();
  const routingQuery = useGroupRouting();
  const groupsQuery = useFunktionsgruppen();

  const groupById = useMemo(() => {
    const map = new Map<string, string | null>();
    (routingQuery.data ?? []).forEach((r) => map.set(r.process, r.funktionsgruppe_id));
    return map;
  }, [routingQuery.data]);
  const groups = (groupsQuery.data?.funktionsgruppen ?? []).map((g) => ({ id: g.id, name: g.name }));

  if (policyQuery.isLoading) return <LoadingState />;
  if (policyQuery.isError) return <ErrorState />;
  const rows = policyQuery.data ?? [];
  if (rows.length === 0) return <EmptyState label="Keine Routing-Prozesse." />;

  return (
    <div className="space-y-3">
      {rows.map((entry) => (
        <RoutingRow
          key={entry.process}
          entry={entry}
          groupId={groupById.get(entry.process) ?? null}
          groups={groups}
          canManage={canManage}
        />
      ))}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-3">
      <h2 className="text-sm font-semibold">{title}</h2>
      {hint != null && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function WorkflowsPage() {
  const { can } = useEffectivePermissions();
  const canReadChannels = can('channel_config.read');
  const canManageChannels = can('channel_config.manage');
  const canReadRouting = can('routing_policy.read');
  const canManageRouting = can('routing_policy.manage');

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />
      <div className="space-y-8">
        {canReadChannels && (
          <section>
            <SectionHeading title="Kanäle" hint="Verfügbarkeit und Priorität der Zustellkanäle." />
            <KanaeleSection canManage={canManageChannels} />
          </section>
        )}
        {canReadChannels && (
          <section>
            <SectionHeading
              title="Kanal-Matrix"
              hint="Standardkanal je Dokumenttyp × Produkt/Sparte."
            />
            <MatrixSection canManage={canManageChannels} />
          </section>
        )}
        {canReadRouting && (
          <section>
            <SectionHeading
              title="Routing"
              hint="Konfidenz-Schwellen und Ziel-Funktionsgruppe je Prozess."
            />
            <RoutingSection canManage={canManageRouting} />
          </section>
        )}
        {!canReadChannels && !canReadRouting && (
          <EmptyState label="Für die Workflow-Konfiguration fehlt Ihnen die Berechtigung." />
        )}
      </div>
    </div>
  );
}
